// src/client/components/RouteErrorBoundary.tsx
//
// Phase post-13a: route 全体の errorElement.
//
// 主要目的: deploy 直後に旧 index.html を持つ browser が、 hash 名が変わった
// chunk file を fetch して 404 を踏む問題 (Vite + React Router + Cloudflare Pages
// の典型) を 自動回復する.
//
// 動作:
//   1. error message が chunk load 失敗パターンに一致するか判定
//   2. 初回失敗: sessionStorage flag set + window.location.reload()
//   3. 2 回連続失敗 (loop): flag 残ったまま → 手動 reload UI + 状況説明
//   4. それ以外の error: 通常 error UI (stack + reload button + Home リンク)

import { useEffect, useRef } from "react";
import { useRouteError, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const RELOAD_FLAG = "chunkReloadAttempted";

/**
 * chunk 動的 import 失敗の判定. 主要 browser の error message を網羅:
 *  - Chrome / Edge: "Failed to fetch dynamically imported module"
 *  - Firefox: "error loading dynamically imported module"
 *  - Safari: "Importing a module script failed"
 */
function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : typeof err === "string" ? err : "";
  if (!msg) return false;
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("Loading chunk") || // webpack 互換 (使ってないが念のため)
    msg.includes("ChunkLoadError")
  );
}

export function RouteErrorBoundary() {
  const err = useRouteError();
  const { t } = useTranslation();
  const chunkErr = isChunkLoadError(err);
  // render 時点で sessionStorage を読む (state 不要、 effect 内 reload で必ず unmount)
  const reloadAttempted = useRef<boolean>(false);
  try {
    reloadAttempted.current = sessionStorage.getItem(RELOAD_FLAG) === "1";
  } catch {
    // ignore (sessionStorage 不可、 reload しない方が安全)
    reloadAttempted.current = true;
  }

  useEffect(() => {
    if (!chunkErr) return;
    if (reloadAttempted.current) return; // ループ防止
    try {
      sessionStorage.setItem(RELOAD_FLAG, "1");
    } catch {
      // sessionStorage 不可なら直接 reload (private mode 等)
    }
    // 反映まで 50ms wait (flag の永続化を確実に)
    const handle = window.setTimeout(() => {
      window.location.reload();
    }, 50);
    return () => window.clearTimeout(handle);
  }, [chunkErr]);

  // chunk 失敗 + 自動 reload 中 → 説明 + spinner
  if (chunkErr && !reloadAttempted.current) {
    return (
      <div className="mx-auto mt-10 max-w-md rounded-lg border border-amber-200 bg-amber-50 p-6 text-center text-sm text-amber-900">
        <p className="font-semibold">{t("routeError.chunk.reloading.title")}</p>
        <p className="mt-2 text-xs">{t("routeError.chunk.reloading.body")}</p>
      </div>
    );
  }

  // chunk 失敗 + 既に reload 試行済 (ループ) → 手動 reload UI
  if (chunkErr && reloadAttempted.current) {
    return (
      <div className="mx-auto mt-10 max-w-md rounded-lg border border-red-200 bg-red-50 p-6 text-sm">
        <p className="font-semibold text-red-900">{t("routeError.chunk.loop.title")}</p>
        <p className="mt-2 text-xs text-red-800">{t("routeError.chunk.loop.body")}</p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* noop */ }
              window.location.reload();
            }}
            className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:opacity-90"
          >
            {t("routeError.action.reload")}
          </button>
          <Link
            to="/"
            onClick={() => { try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* noop */ } }}
            className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
          >
            {t("routeError.action.home")}
          </Link>
        </div>
      </div>
    );
  }

  // 通常 error
  const message =
    err instanceof Error ? err.message : typeof err === "string" ? err : "Unknown error";
  return (
    <div className="mx-auto mt-10 max-w-md rounded-lg border border-slate-200 bg-white p-6 text-sm">
      <p className="font-semibold text-slate-900">{t("routeError.generic.title")}</p>
      <p className="mt-2 break-all text-xs text-slate-600">{message}</p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:opacity-90"
        >
          {t("routeError.action.reload")}
        </button>
        <Link
          to="/"
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          {t("routeError.action.home")}
        </Link>
      </div>
    </div>
  );
}

/**
 * 成功 navigation で reload flag を解除する hook.
 * RouterProvider 内の Layout で利用すると、 user が正常に画面遷移できた時点で
 * loop counter を reset できる.
 */
export function useClearChunkReloadFlag() {
  useEffect(() => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      // noop
    }
  }, []);
}
